import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Shield, Download, ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

function SecurityLogPage() {
  const navigate = useNavigate();
  const { theme, currentThemeName } = useTheme();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const dark = currentThemeName === 'dark';
  const beige = currentThemeName === 'beige';
  const cardBg = dark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
    : beige
    ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)';
  const cardBorder = dark
    ? '1px solid rgba(59, 130, 246, 0.2)'
    : beige
    ? '1px solid rgba(139, 69, 19, 0.15)'
    : '1px solid rgba(59, 130, 246, 0.1)';
  const text = dark ? 'white' : theme.text;
  const muted = dark ? '#e5e7eb' : beige ? '#8d6e63' : '#6b7280';

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${getApiBaseUrl()}/api/auth/security-incident`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not load the log');
        setReport(data);
      })
      .catch((err) => setError(err.message));
  }, []);

  const download = () => {
    const token = localStorage.getItem('authToken');
    setDownloading(true);
    fetch(`${getApiBaseUrl()}/api/auth/security-incident/download`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'login-incident-2026-10-06.log';
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err.message))
      .finally(() => setDownloading(false));
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: dark ? '#0f172a' : beige ? '#f5f5dc' : '#ffffff',
      color: text,
      padding: 20,
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 300,
        background: dark
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)'
          : beige
          ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.08) 0%, rgba(160, 82, 45, 0.08) 100%)'
          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 51, 234, 0.08) 100%)'
      }} />
      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 0', marginBottom: 8, gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              onClick={() => navigate('/fullchat')}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/fullchat'); }}
              title="Go to main page"
              style={{
                fontSize: 28,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                cursor: 'pointer'
              }}
            >
              Oakland AI
            </div>
            <div style={{ width: 1, height: 40, background: theme.border }} />
            <div>
              <div style={{ fontSize: 13, color: muted, marginBottom: 4 }}>
                <button type="button" onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', padding: 0, color: '#3b82f6', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Admin
                </button>
                <span> / Security log</span>
              </div>
              <h1 style={{ fontSize: 32, fontWeight: 700, margin: 0, color: text }}>Login incident</h1>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={{
              padding: '12px 20px',
              borderRadius: 12,
              background: dark
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              color: dark ? '#60a5fa' : '#3b82f6',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <ArrowLeft size={16} />
            Admin page
          </button>
        </div>

        <p style={{ color: muted, fontSize: 16, marginTop: 0, marginBottom: 24 }}>
          What filled the disk, why login failed, and what is blocking it now.
        </p>

        {error && <p style={{ color: '#ef4444' }}>{error}</p>}
        {!report && !error && <p style={{ color: muted }}>Loading…</p>}

        {report && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 24 }}>
              {[
                ['When it started', 'October 2, 2026'],
                ['When login broke', 'October 6, 2026'],
                ['What you see below', 'A sample, not the full log']
              ].map(([label, value]) => (
                <div key={label} style={{ background: cardBg, border: cardBorder, borderRadius: 20, padding: 22 }}>
                  <div style={{ fontSize: 13, color: muted, marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ background: cardBg, border: cardBorder, borderRadius: 20, padding: 28, marginBottom: 24 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <AlertTriangle color="#ef4444" size={22} />
                <h2 style={{ margin: 0, fontSize: 20 }}>What happened</h2>
              </div>
              <p style={{ color: muted, lineHeight: 1.6, marginTop: 0 }}>
                {report.summary} Someone added 1,746 scheduled tasks on the LiteSpeed account. Each one tried to save an SSH key for logging into the server. The tasks failed, so no key was saved. Every failure sent an email, and those emails plus the logs of them grew to about 100GB. The disk hit 100% full, so login could not save a response and showed an error.
              </p>
              <p style={{ color: muted, lineHeight: 1.6 }}>
                The lines below are from 7:39 to 7:40pm UTC on October 6, the minute before the tasks were removed. There were 3,492 of those lines in that minute alone, all the same action with a different key. The logs from October 2 through earlier on October 6 are gone because they were the files that filled the disk. They were cleared so login would work again. The keys themselves are hidden in this view.
              </p>
            </div>

            <div style={{ background: cardBg, border: cardBorder, borderRadius: 20, padding: 28, marginBottom: 24 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <Shield color="#10b981" size={22} />
                <h2 style={{ margin: 0, fontSize: 20 }}>What is in place now</h2>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {report.mitigations.map((item) => (
                  <li key={item.text} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, color: muted, lineHeight: 1.5 }}>
                    <CheckCircle2 size={18} color={item.inPlace ? '#10b981' : '#f59e0b'} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span>{item.inPlace ? 'In place. ' : 'Not confirmed. '}{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>Log sample</h2>
              <button
                type="button"
                onClick={download}
                disabled={downloading}
                style={{
                  padding: '12px 18px',
                  borderRadius: 12,
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <Download size={16} />
                {downloading ? 'Downloading…' : 'Download this sample'}
              </button>
            </div>
            <pre style={{
              margin: 0,
              padding: 20,
              borderRadius: 16,
              background: '#0f172a',
              color: '#e2e8f0',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: 12.5,
              lineHeight: 1.55,
              border: '1px solid rgba(59, 130, 246, 0.25)'
            }}>{report.excerpt}</pre>
          </>
        )}
      </div>
    </div>
  );
}

export default SecurityLogPage;
